import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { RUBRIC_SCALE_MIN, type RubricView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { SearchInput } from '../../../../components/Input';
import { Select } from '../../../../components/Select';
import { SkeletonLines } from '../../../../components/Skeleton';
import { EmptyState, ForbiddenState, NotFoundState } from '../../../../components/system-states/SystemState';
import { formatDate } from '../../../../lib/format';
import { useMediaQuery } from '../../../../lib/use-media-query';
import { fuzzyFilter } from '../../../../lib/fuzzy';
import { useCanEdit, useCanView } from '../../../../stores/session';
import { useRubric, useRubrics } from '../../api/evaluation-api';
import { RubricEditor } from './RubricEditor';
import styles from './Evaluation.module.css';

/**
 * ADMIN-02 (F19.02), admin redesign batch 2 Option A: the rubrics on the left, the open one on the right. At laptop width
 * and below the list folds into a picker above the editor. With no rubric chosen, the first one opens — the lazy path is
 * one click to any rubric, none to the first.
 *
 * Since ADR 0077 nobody marks a rubric's lines: a round gets one overall mark. A rubric still reaches live work through
 * interview kits (their questions are grouped under its lines) and the older rounds and scorecards that name a version. The
 * page says so in plain words, and draws no weight bar.
 */
export function RubricsWorkspace({ selectedId }: { readonly selectedId: string | null }): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const rubrics = useRubrics(canView);
  const openDetail = useRubric(selectedId === 'new' ? null : selectedId, canView);
  const navigate = useNavigate();
  const isNarrow = useMediaQuery('(max-width: 1180px)');
  const [query, setQuery] = useState('');

  const rows = useMemo(() => rubrics.data ?? [], [rubrics.data]);
  const shown = useMemo(() => fuzzyFilter(query, rows, (row) => `${row.name} ${row.criteria.map((line) => line.label).join(' ')}`).map((result) => result.item), [query, rows]);

  if (!canView) return <ForbiddenState resource="rubrics" contact="Your admin" />;

  const isNew = selectedId === 'new';
  const openId = isNew ? null : (selectedId ?? rows[0]?.id ?? null);
  // An older version is not in the list (current versions only); its family still marks the rubric it belongs to.
  const openFamily = openDetail.data?.familyId ?? rows.find((row) => row.id === openId)?.familyId ?? null;
  const openCurrentId = rows.find((row) => row.familyId === openFamily)?.id ?? null;

  const newButton = canEdit ? (
    <Button variant="solid" size="sm" icon={Plus} onClick={() => void navigate({ to: '/admin/rubrics/$rubricId', params: { rubricId: 'new' } })}>
      New rubric
    </Button>
  ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          Rubrics {rubrics.data && <span className={styles.count}>{rows.length}</span>}
        </h1>
        <div className={styles.headActions}>{rows.length > 0 && !isNew && newButton}</div>
      </header>
      <p className={styles.lede}>
        The lines an interview kit's questions are grouped under, with what good looks like on each. Saving writes a new version; rounds
        already using an older one keep it.
      </p>
      <p className={styles.plainNote}>
        <strong>Nobody marks these lines any more.</strong> Since 01-11-2026 each interviewer gives one overall mark from 1 to 5, and the
        round's result weighs those marks by the panel's shares. A rubric now reaches a job through its interview kits: interviewers see
        its lines as the topics of the kit's questions. Lines carry no weight, and a must-pass line never blocks anyone.
      </p>

      {rubrics.isPending ? (
        <SkeletonLines count={8} />
      ) : rows.length === 0 && !isNew ? (
        <EmptyState isInset title="No rubrics yet" body="A rubric names the lines an interview kit's questions are grouped under." action={newButton ?? undefined} />
      ) : (
        <div className={styles.split}>
          {isNarrow ? (
            <div className={styles.picker}>
              <Select
                label="Rubric"
                placeholder="Choose a rubric…"
                value={isNew ? null : openCurrentId}
                options={rows.map((row) => ({ value: row.id, label: row.name, description: `Version ${row.versionNumber} · ${row.criteria.length} lines` }))}
                onChange={(rubricId) => void navigate({ to: '/admin/rubrics/$rubricId', params: { rubricId } })}
              />
            </div>
          ) : (
            <RubricList rows={shown} total={rows.length} openFamily={openFamily} query={query} onQuery={setQuery} />
          )}
          {isNew ? <RubricEditor key="new" rubric={null} current={null} /> : <OpenRubric id={openId} list={rows} />}
        </div>
      )}
    </div>
  );
}

/** The open version, seeded from the list so switching never flashes a skeleton (§5a rule 2), then read on its own. */
function OpenRubric({ id, list }: { readonly id: string | null; readonly list: readonly RubricView[] }): ReactNode {
  const listed = list.find((row) => row.id === id);
  const detail = useRubric(id);
  const rubric = detail.data ?? listed ?? null;
  if (id === null) return null;
  if (!rubric) return detail.isError ? <NotFoundState /> : <SkeletonLines count={8} />;
  const current = list.find((row) => row.familyId === rubric.familyId) ?? null;
  return <RubricEditor key={rubric.id} rubric={rubric} current={current} />;
}

function RubricList({
  rows,
  total,
  openFamily,
  query,
  onQuery,
}: {
  readonly rows: readonly RubricView[];
  readonly total: number;
  readonly openFamily: string | null;
  readonly query: string;
  readonly onQuery: (query: string) => void;
}): ReactNode {
  // 06-qol §2: every list is arrow-key navigable.
  function onKeyDown(event: KeyboardEvent<HTMLUListElement>): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>('a'));
    const at = links.findIndex((link) => link === document.activeElement);
    const next = links[Math.min(Math.max(at + (event.key === 'ArrowDown' ? 1 : -1), 0), links.length - 1)];
    if (!next) return;
    event.preventDefault();
    next.focus();
  }

  return (
    <nav className={styles.list} aria-label="Rubrics">
      <SearchInput aria-label="Find a rubric" placeholder="Find a rubric" value={query} onChange={(event) => onQuery(event.target.value)} onClear={() => onQuery('')} />
      {rows.length === 0 ? (
        <p className={styles.quiet}>
          No rubric matches "{query}" among {total}.{' '}
          <button type="button" className={styles.linkButton} onClick={() => onQuery('')}>
            Clear the search
          </button>
        </p>
      ) : (
        <ul className={styles.listItems} onKeyDown={onKeyDown}>
          {rows.map((row) => (
            <li key={row.id}>
              <Link
                to="/admin/rubrics/$rubricId"
                params={{ rubricId: row.id }}
                className={styles.listItem}
                aria-current={row.familyId === openFamily ? 'page' : undefined}
              >
                <span className={styles.listName}>
                  {row.name} <Chip tone="neutral">Version {row.versionNumber}</Chip>
                </span>
                <span className={styles.listMeta}>
                  Scored {RUBRIC_SCALE_MIN}–{row.scaleMax} · used in {row.timesUsed} round{row.timesUsed === 1 ? '' : 's'} · updated {formatDate(row.updatedAt)}
                </span>
                <span className={styles.listLines}>{row.criteria.map((line) => line.label).join(' · ')}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
